// src/components/EcommerceLoadingScreen.js
import React, { useState, useEffect } from "react";
import { Box, Typography, styled, keyframes } from "@mui/material";

// Keyframe animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-5deg); }
  50% { transform: translateY(-30px) rotate(5deg); }
`;

const gentleFloat = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(3deg); }
`;

const bounceIn = keyframes`
  0% { transform: scale(0) rotate(0deg); opacity: 0; }
  50% { transform: scale(1.1) rotate(180deg); opacity: 0.8; }
  100% { transform: scale(1) rotate(360deg); opacity: 1; }
`;

const floatAround = keyframes`
  0% { transform: translate(0px, 0px) rotate(0deg); }
  25% { transform: translate(40px, -40px) rotate(90deg); }
  50% { transform: translate(-30px, -80px) rotate(180deg); }
  75% { transform: translate(-50px, -30px) rotate(270deg); }
  100% { transform: translate(0px, 0px) rotate(360deg); }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  50% { opacity: 1; transform: scale(1.5) rotate(180deg); }
`;

const wiggle = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-8deg); }
  75% { transform: rotate(8deg); }
`;

const textGlow = keyframes`
  0% { filter: brightness(1) drop-shadow(0 0 5px rgba(11, 28, 72, 0.5)); }
  100% { filter: brightness(1.2) drop-shadow(0 0 15px rgba(11, 28, 72, 0.8)); }
`;

const progressLoad = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`;

const blueShimmer = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const glassFloat = keyframes`
  0%, 100% { transform: translateY(0px); box-shadow: 0 8px 32px rgba(11, 28, 72, 0.37); }
  50% { transform: translateY(-8px); box-shadow: 0 12px 40px rgba(11, 28, 72, 0.5); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const sway = keyframes`
  0%, 100% { transform: translateX(0px) rotate(-2deg); }
  50% { transform: translateX(10px) rotate(2deg); }
`;

// Styled components
const LoadingContainer = styled(Box)(({ fade }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  background: `linear-gradient(135deg, 
    #F0F8FF 0%, 
    #E6F0FA 25%, 
    #C5DDF5 50%, 
    #E6F0FA 75%, 
    #F0F8FF 100%)`,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
  opacity: fade ? 0 : 1,
  transition: "opacity 0.8s ease-out",
  pointerEvents: fade ? "none" : "all",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(11, 28, 72, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 80%, rgba(0, 34, 102, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 60% 30%, rgba(66, 165, 245, 0.1) 0%, transparent 50%)
    `,
    zIndex: 0,
  },
}));

const MainContent = styled(Box)({
  background: "rgba(255, 255, 255, 0.25)",
  backdropFilter: "blur(20px)",
  border: "1px solid rgba(255, 255, 255, 0.18)",
  borderRadius: "24px",
  boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  padding: "40px 50px",
  textAlign: "center",
  animation: `${glassFloat} 3s ease-in-out infinite`,
  zIndex: 2,
  "@media (max-width: 768px)": {
    padding: "30px 25px",
    margin: "0 20px",
  },
});

const MainIcon = styled(Box)({
  animation: `${float} 3s ease-in-out infinite`,
  marginBottom: 20,
  filter: "drop-shadow(0 10px 20px rgba(11, 28, 72, 0.3))",
  zIndex: 1,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  "& img": {
    height: "140px",
    width: "auto",
    objectFit: "contain",
    "@media (max-width: 768px)": {
      height: "100px",
    },
    "@media (max-width: 480px)": {
      height: "80px",
    },
  },
});

const FloatingProduct = styled(Box)(
  ({ delay, duration, size, animationType }) => {
    const getAnimation = () => {
      switch (animationType) {
        case "spin":
          return `${bounceIn} 1s ease-out ${delay}s both, ${spin} ${duration}s linear infinite ${
            delay + 1
          }s`;
        case "sway":
          return `${bounceIn} 1s ease-out ${delay}s both, ${sway} ${duration}s ease-in-out infinite ${
            delay + 1
          }s`;
        case "float":
          return `${bounceIn} 1s ease-out ${delay}s both, ${gentleFloat} ${duration}s ease-in-out infinite ${
            delay + 1
          }s`;
        default:
          return `${bounceIn} 1s ease-out ${delay}s both, ${floatAround} ${duration}s ease-in-out infinite ${
            delay + 1
          }s`;
      }
    };

    return {
      position: "absolute",
      fontSize: size || "50px",
      animation: getAnimation(),
      zIndex: 1,
      filter: "drop-shadow(0 5px 15px rgba(11, 28, 72, 0.2))",
      left: `${Math.random() * 80 + 10}%`,
      top: `${Math.random() * 70 + 10}%`,
    };
  }
);

const Sparkle = styled(Box)(({ delay, size }) => ({
  position: "absolute",
  fontSize: size || "20px",
  animation: `${sparkle} 2s ease-in-out infinite ${delay}s`,
  zIndex: 0,
  left: `${Math.random() * 90 + 5}%`,
  top: `${Math.random() * 80 + 10}%`,
  filter: "drop-shadow(0 0 10px rgba(255, 255, 255, 0.8))",
}));

const LoadingText = styled(Typography)({
  fontSize: "2.8rem",
  fontWeight: "bold",
  background: "linear-gradient(45deg, #001A4D, #0072CE, #60A5FA, #C5DDF5)",
  backgroundSize: "300% 300%",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  animation: `${blueShimmer} 3s ease-in-out infinite, ${textGlow} 2s ease-in-out infinite alternate`,
  marginBottom: 10,
  zIndex: 1,
  textAlign: "center",
  "@media (max-width: 768px)": {
    fontSize: "2rem",
  },
});

const LoadingSubtitle = styled(Typography)({
  fontSize: "1.3rem",
  color: "#0B1C48",
  marginBottom: 30,
  fontWeight: 600,
  textAlign: "center",
  zIndex: 1,
  filter: "drop-shadow(0 2px 4px rgba(11, 28, 72, 0.1))",
  "@media (max-width: 768px)": {
    fontSize: "1.1rem",
    padding: "0 10px",
  },
});

const ProgressContainer = styled(Box)({
  width: 320,
  height: 12,
  background: "rgba(255, 255, 255, 0.3)",
  backdropFilter: "blur(10px)",
  borderRadius: 20,
  overflow: "hidden",
  marginTop: 25,
  zIndex: 1,
  boxShadow: "inset 0 2px 4px rgba(11, 28, 72, 0.1)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  "@media (max-width: 768px)": {
    width: 260,
  },
});

const ProgressFill = styled(Box)({
  height: "100%",
  background:
    "linear-gradient(90deg, #001A4D, #0072CE, #60A5FA, #C5DDF5, #001A4D)",
  backgroundSize: "200% 100%",
  borderRadius: 20,
  animation: `${progressLoad} 3s ease-in-out, ${blueShimmer} 2s ease-in-out infinite`,
  position: "relative",
  boxShadow: "0 0 20px rgba(11, 28, 72, 0.5)",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(90deg, 
      transparent 0%, 
      rgba(255,255,255,0.6) 50%, 
      transparent 100%)`,
    animation: `${blueShimmer} 1.5s ease-in-out infinite`,
  },
});

const LoadingMessage = styled(Typography)({
  fontSize: "1rem",
  color: "#0B1C48",
  marginTop: 20,
  fontWeight: 500,
  textAlign: "center",
  zIndex: 1,
  animation: `${wiggle} 2s ease-in-out infinite`,
  background: "rgba(255, 255, 255, 0.2)",
  backdropFilter: "blur(10px)",
  padding: "8px 16px",
  borderRadius: "20px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  display: "inline-block",
});

const FloatingGlassCard = styled(Box)(({ delay, left, top, size }) => ({
  position: "absolute",
  left: `${left}%`,
  top: `${top}%`,
  width: size || "70px",
  height: size || "70px",
  background: "rgba(255, 255, 255, 0.15)",
  backdropFilter: "blur(15px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(11, 28, 72, 0.2)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "32px",
  animation: `${gentleFloat} 4s ease-in-out infinite ${delay}s, ${glassFloat} 3s ease-in-out infinite ${delay}s`,
  zIndex: 1,
}));

const WhaleLoadingScreen = ({ onLoadingComplete }) => {
  const [fade, setFade] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(0);

  const messages = [
    "🛍️ Loading amazing products...",
    "📦 Preparing your shopping experience...",
    "✨ Setting up the store...",
    "🎉 Almost ready to shop!",
  ];

  useEffect(() => {
    // Change message every 750ms
    const messageTimer = setInterval(() => {
      setCurrentMessage((prev) => (prev + 1) % messages.length);
    }, 750);

    const timer = setTimeout(() => {
      setFade(true);
      setTimeout(() => {
        onLoadingComplete();
      }, 800);
    }, 3000);

    return () => {
      clearTimeout(timer);
      clearInterval(messageTimer);
    };
  }, [onLoadingComplete]);

  // Ecommerce products in glass cards (larger, featured items)
  const floatingGlassProducts = [
    { emoji: "👕", delay: 0.2, left: 15, top: 20, size: "80px" },
    { emoji: "👗", delay: 0.5, left: 85, top: 15, size: "75px" },
    { emoji: "👟", delay: 0.8, left: 10, top: 70, size: "70px" },
    { emoji: "🎒", delay: 1.1, left: 90, top: 75, size: "78px" },
    { emoji: "⌚", delay: 1.4, left: 20, top: 45, size: "65px" },
    { emoji: "👓", delay: 1.7, left: 80, top: 50, size: "68px" },
    { emoji: "🧢", delay: 2.0, left: 5, top: 35, size: "72px" },
    { emoji: "👔", delay: 0.3, left: 95, top: 40, size: "76px" },
  ];

  // Regular floating products (smaller, more variety)
  const floatingProducts = [
    {
      emoji: "👕",
      delay: 0.3,
      duration: 12,
      size: "40px",
      animationType: "sway",
    },
    {
      emoji: "👖",
      delay: 0.7,
      duration: 8,
      size: "42px",
      animationType: "float",
    },
    {
      emoji: "👗",
      delay: 1.1,
      duration: 10,
      size: "38px",
      animationType: "spin",
    },
    {
      emoji: "👚",
      delay: 1.5,
      duration: 9,
      size: "36px",
      animationType: "sway",
    },
    {
      emoji: "👟",
      delay: 0.4,
      duration: 11,
      size: "44px",
      animationType: "float",
    },
    {
      emoji: "👠",
      delay: 0.9,
      duration: 7,
      size: "35px",
      animationType: "spin",
    },
    {
      emoji: "🥿",
      delay: 1.3,
      duration: 8,
      size: "37px",
      animationType: "sway",
    },
    {
      emoji: "👢",
      delay: 1.8,
      duration: 9,
      size: "41px",
      animationType: "float",
    },
    {
      emoji: "🎒",
      delay: 0.6,
      duration: 10,
      size: "39px",
      animationType: "spin",
    },
    {
      emoji: "👜",
      delay: 1.0,
      duration: 8,
      size: "35px",
      animationType: "sway",
    },
    {
      emoji: "🧳",
      delay: 1.4,
      duration: 11,
      size: "43px",
      animationType: "float",
    },
    {
      emoji: "⌚",
      delay: 0.8,
      duration: 9,
      size: "33px",
      animationType: "spin",
    },
    {
      emoji: "👓",
      delay: 1.2,
      duration: 7,
      size: "34px",
      animationType: "sway",
    },
    {
      emoji: "🕶️",
      delay: 1.6,
      duration: 10,
      size: "36px",
      animationType: "float",
    },
    {
      emoji: "🧢",
      delay: 0.5,
      duration: 8,
      size: "38px",
      animationType: "spin",
    },
    {
      emoji: "👒",
      delay: 1.7,
      duration: 9,
      size: "37px",
      animationType: "sway",
    },
    {
      emoji: "🎧",
      delay: 0.2,
      duration: 11,
      size: "40px",
      animationType: "float",
    },
    {
      emoji: "📱",
      delay: 1.9,
      duration: 8,
      size: "32px",
      animationType: "spin",
    },
    {
      emoji: "💻",
      delay: 0.7,
      duration: 10,
      size: "42px",
      animationType: "sway",
    },
    {
      emoji: "⚽",
      delay: 1.1,
      duration: 7,
      size: "38px",
      animationType: "spin",
    },
    {
      emoji: "🏀",
      delay: 1.5,
      duration: 9,
      size: "40px",
      animationType: "float",
    },
    {
      emoji: "🎾",
      delay: 0.3,
      duration: 8,
      size: "34px",
      animationType: "spin",
    },
  ];

  // Sparkles for extra magic
  const sparkles = Array.from({ length: 12 }, (_, i) => (
    <Sparkle key={i} delay={i * 0.3} size={"16px"}>
      ✨
    </Sparkle>
  ));

  return (
    <LoadingContainer fade={fade}>
      {/* Floating glass cards with featured products */}
      {floatingGlassProducts.map((product, index) => (
        <FloatingGlassCard
          key={index}
          delay={product.delay}
          left={product.left}
          top={product.top}
          size={product.size}
        >
          {product.emoji}
        </FloatingGlassCard>
      ))}

      {/* Regular floating products */}
      {floatingProducts.map((product, index) => (
        <FloatingProduct
          key={index}
          delay={product.delay}
          duration={product.duration}
          size={product.size}
          animationType={product.animationType}
        >
          {product.emoji}
        </FloatingProduct>
      ))}

      {/* Sparkles */}
      {sparkles}

      {/* Main content in glass card */}
      <MainContent>
        <LoadingText variant="h2">InfinityWhale</LoadingText>

        <LoadingSubtitle>
          Your ultimate shopping destination! 🛍️
        </LoadingSubtitle>

        <MainIcon>
          <img src="/infinitywhalelogo.png" alt="InfinityWhale Logo" />
        </MainIcon>

        <ProgressContainer>
          <ProgressFill />
        </ProgressContainer>

        <LoadingMessage>{messages[currentMessage]}</LoadingMessage>
      </MainContent>
    </LoadingContainer>
  );
};

export default WhaleLoadingScreen;
